import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { toast } from 'react-toastify';
import { CalendarPlus, Pencil, Plus, ToggleLeft, ToggleRight, Trash2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { ProfessionalPaymentService } from '../../services/ProfessionalPaymentService';
import { ProfessionalFinancialRoleService } from '../../services/ProfessionalFinancialRoleService';
import { archiveFinancialCatalogRecord } from '../../services/FinancialCatalogService';
import {
  ProfessionalCompensationModel,
  ProfessionalFinancialRole,
  ProfessionalFinancialRoleStatus,
  ProfessionalFinancialRoleType,
} from '../../types/professional-financial-role';

type ProfessionalOption = { doctorId: string; displayName: string };

type RoleForm = {
  professionalId: string;
  financialRoleType: ProfessionalFinancialRoleType;
  compensationModel: ProfessionalCompensationModel;
  fixedAmount: string;
  effectiveFrom: string;
  effectiveUntil: string;
  roleStatus: ProfessionalFinancialRoleStatus;
  notes: string;
};

const today = () => new Date().toISOString().slice(0, 10);
const currentMonth = () => new Date().toISOString().slice(0, 7);
const emptyForm = (): RoleForm => ({
  professionalId: '',
  financialRoleType: 'fisioterapeuta',
  compensationModel: 'por_sessao',
  fixedAmount: '',
  effectiveFrom: today(),
  effectiveUntil: '',
  roleStatus: 'active',
  notes: '',
});

const roleLabels: Record<ProfessionalFinancialRoleType, string> = {
  fisioterapeuta: 'Fisioterapeuta',
  socio_proprietario: 'Sócio / proprietário',
};

const modelLabels: Record<ProfessionalCompensationModel, string> = {
  por_sessao: 'Por sessão',
  fixo_mensal: 'Fixo mensal',
  pro_labore: 'Pró-labore',
  retirada_variavel: 'Retirada variável',
};

const ProfessionalFinancialRolesPage = ({ embedded = false, hideGeneration = false }: { embedded?: boolean; hideGeneration?: boolean }) => {
  const { selectedClient } = useAuth();
  const clientId = selectedClient?.clientId;
  const canEdit = selectedClient?.userRole === 'admin';
  const [roles, setRoles] = useState<ProfessionalFinancialRole[]>([]);
  const [professionals, setProfessionals] = useState<ProfessionalOption[]>([]);
  const [form, setForm] = useState<RoleForm>(() => emptyForm());
  const [editingRoleId, setEditingRoleId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [databaseMessage, setDatabaseMessage] = useState('');
  const [generationMonth, setGenerationMonth] = useState(currentMonth());
  const [generationDueDate, setGenerationDueDate] = useState('');
  const [variableAmounts, setVariableAmounts] = useState<Record<string, string>>({});
  const [generating, setGenerating] = useState(false);

  const professionalNameById = useMemo(
    () => new Map(professionals.map((professional) => [professional.doctorId, professional.displayName])),
    [professionals]
  );

  const loadData = async () => {
    if (!clientId) return;
    setLoading(true);
    setDatabaseMessage('');
    try {
      const [loadedRoles, loadedProfessionals] = await Promise.all([
        ProfessionalFinancialRoleService.listByClient(clientId),
        ProfessionalPaymentService.getProfessionalsForFinancialRoles(clientId),
      ]);
      setRoles(loadedRoles);
      setProfessionals(
        loadedProfessionals
          .filter((professional) => professional.doctorId)
          .map((professional) => ({ doctorId: professional.doctorId as string, displayName: professional.displayName }))
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Não foi possível carregar os vínculos financeiros.';
      setDatabaseMessage(message);
      setRoles([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void loadData(); }, [clientId]);

  const resetForm = () => {
    setEditingRoleId(null);
    setForm(emptyForm());
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canEdit || saving) return;
    if (!clientId || !form.professionalId) {
      toast.error('Selecione um profissional.');
      return;
    }
    const needsAmount = form.compensationModel === 'fixo_mensal' || form.compensationModel === 'pro_labore';
    const amount = form.fixedAmount.trim() === '' ? null : Number(form.fixedAmount);
    if (needsAmount && (amount === null || !Number.isFinite(amount) || amount < 0)) {
      toast.error('Informe o valor fixo do vínculo.');
      return;
    }
    if (form.effectiveUntil && form.effectiveUntil < form.effectiveFrom) {
      toast.error('O fim da vigência não pode ser anterior ao início.');
      return;
    }

    setSaving(true);
    try {
      await ProfessionalFinancialRoleService.save({
        clientId,
        financialRoleId: editingRoleId,
        professionalId: form.professionalId,
        financialRoleType: form.financialRoleType,
        compensationModel: form.compensationModel,
        fixedAmount: amount,
        effectiveFrom: form.effectiveFrom,
        effectiveUntil: form.effectiveUntil || null,
        roleStatus: form.roleStatus,
        notes: form.notes,
      });
      toast.success('Vínculo financeiro salvo.');
      resetForm();
      await loadData();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Não foi possível salvar o vínculo financeiro.');
    } finally {
      setSaving(false);
    }
  };

  const editRole = (role: ProfessionalFinancialRole) => {
    setEditingRoleId(role.financialRoleId);
    setForm({
      professionalId: role.professionalId,
      financialRoleType: role.financialRoleType,
      compensationModel: role.compensationModel,
      fixedAmount: role.fixedAmount === null ? '' : String(role.fixedAmount),
      effectiveFrom: role.effectiveFrom,
      effectiveUntil: role.effectiveUntil || '',
      roleStatus: role.roleStatus,
      notes: role.notes || '',
    });
  };

  const toggleRole = async (role: ProfessionalFinancialRole) => {
    if (!clientId || !canEdit || saving) return;
    setSaving(true);
    try {
      await ProfessionalFinancialRoleService.setStatus(clientId, role.financialRoleId, role.roleStatus === 'active' ? 'inactive' : 'active');
      toast.success('Status do vínculo atualizado.');
      await loadData();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Não foi possível atualizar o vínculo.');
    } finally { setSaving(false); }
  };

  const removeRole = async (role: ProfessionalFinancialRole) => {
    if (!clientId || !canEdit || saving) return;
    const name = professionalNameById.get(role.professionalId) || 'profissional';
    if (!window.confirm(`Excluir o vínculo de ${name}? Ele deixará de ser utilizado em novas obrigações e repasses. Os valores e lançamentos anteriores serão preservados.`)) return;
    setSaving(true);
    try {
      await archiveFinancialCatalogRecord({ clientId, kind: 'professional_role', recordId: role.financialRoleId });
      if (editingRoleId === role.financialRoleId) resetForm();
      toast.success('Vínculo excluído. Histórico preservado.');
      await loadData();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Não foi possível excluir o vínculo.');
    } finally { setSaving(false); }
  };

  const generateMonthlyExpenses = async () => {
    if (!clientId) return;
    const parsedAmounts: Record<string, number> = {};
    for (const role of roles.filter((item) => item.roleStatus === 'active' && item.compensationModel === 'retirada_variavel')) {
      const raw = variableAmounts[role.financialRoleId]?.trim();
      if (!raw) continue;
      const amount = Number(raw);
      if (!Number.isFinite(amount) || amount <= 0) {
        toast.error(`Informe um valor maior que zero para ${professionalNameById.get(role.professionalId) || 'a retirada variável'}.`);
        return;
      }
      parsedAmounts[role.financialRoleId] = amount;
    }
    setGenerating(true);
    try {
      const result = await ProfessionalFinancialRoleService.generateMonthlyExpenses({
        clientId,
        competence: generationMonth,
        dueDate: generationDueDate || null,
        variableAmounts: parsedAmounts,
      });
      const missing = result.missingCount > 0 ? ` ${result.missingCount} vínculo(s) ficaram pendentes por falta de valor.` : '';
      toast.success(`${result.createdCount} obrigação(ões) criada(s); ${result.existingCount} já existente(s).${missing}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Não foi possível gerar as obrigações mensais.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-6">
      {!embedded && <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-gray-800">Vínculos financeiros</h2>
          <p className="text-sm text-gray-500">A mesma pessoa pode ter um vínculo de atendimento e outro de sócio/proprietário.</p>
        </div>
        <button onClick={resetForm} className="inline-flex items-center rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700">
          <Plus className="mr-2 h-4 w-4" /> Novo vínculo
        </button>
      </div>}

      {databaseMessage && (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          {databaseMessage}
        </div>
      )}

      {!hideGeneration && <section className="rounded-lg border border-primary-200 bg-primary-50 p-5">
        <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-end">
          <div>
            <h3 className="font-semibold text-gray-900">Gerar obrigações mensais</h3>
            <p className="text-sm text-gray-600">Gera despesas para vínculos fixos, pró-labore e retiradas variáveis. Vínculos por sessão permanecem no fluxo de Repasses.</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-[auto_auto_auto] sm:items-end">
            <label className="text-sm font-medium text-gray-700">Competência<input type="month" value={generationMonth} onChange={(event) => setGenerationMonth(event.target.value)} className="mt-1 block rounded-md border-gray-300 bg-white shadow-sm" /></label>
            <label className="text-sm font-medium text-gray-700">Vencimento<input type="date" value={generationDueDate} onChange={(event) => setGenerationDueDate(event.target.value)} className="mt-1 block rounded-md border-gray-300 bg-white shadow-sm" /></label>
            <button type="button" onClick={() => void generateMonthlyExpenses()} disabled={generating} className="inline-flex items-center justify-center rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"><CalendarPlus className="mr-2 h-4 w-4" />{generating ? 'Gerando...' : 'Gerar obrigações'}</button>
          </div>
        </div>
        {roles.some((role) => role.roleStatus === 'active' && role.compensationModel === 'retirada_variavel') && (
          <div className="mt-4 grid gap-3 border-t border-primary-200 pt-4 sm:grid-cols-2 lg:grid-cols-3">
            {roles.filter((role) => role.roleStatus === 'active' && role.compensationModel === 'retirada_variavel').map((role) => (
              <label key={role.financialRoleId} className="text-sm font-medium text-gray-700">Retirada de {professionalNameById.get(role.professionalId) || 'profissional'} (R$)<input type="number" min="0.01" step="0.01" value={variableAmounts[role.financialRoleId] || ''} onChange={(event) => setVariableAmounts((current) => ({ ...current, [role.financialRoleId]: event.target.value }))} className="mt-1 block w-full rounded-md border-gray-300 bg-white shadow-sm" placeholder="Informe para esta competência" /></label>
            ))}
          </div>
        )}
      </section>}

      <div className="grid gap-6 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
        <form onSubmit={handleSubmit} className="space-y-4 rounded-lg border bg-white p-6 shadow-sm">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Pessoa / profissional</label>
            <select value={form.professionalId} onChange={(event) => setForm((current) => ({ ...current, professionalId: event.target.value }))} className="w-full rounded-md border-gray-300 shadow-sm">
              <option value="">Selecione</option>
              {professionals.map((professional) => <option key={professional.doctorId} value={professional.doctorId}>{professional.displayName}</option>)}
            </select>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Tipo de vínculo</label>
              <select value={form.financialRoleType} onChange={(event) => setForm((current) => ({ ...current, financialRoleType: event.target.value as ProfessionalFinancialRoleType }))} className="w-full rounded-md border-gray-300 shadow-sm">
                {Object.entries(roleLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Modelo de remuneração</label>
              <select value={form.compensationModel} onChange={(event) => setForm((current) => ({ ...current, compensationModel: event.target.value as ProfessionalCompensationModel }))} className="w-full rounded-md border-gray-300 shadow-sm">
                {Object.entries(modelLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </select>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Valor fixo (R$)</label>
              <input type="number" min="0" step="0.01" value={form.fixedAmount} onChange={(event) => setForm((current) => ({ ...current, fixedAmount: event.target.value }))} className="w-full rounded-md border-gray-300 shadow-sm" placeholder="Não se aplica por sessão" />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Status</label>
              <select value={form.roleStatus} onChange={(event) => setForm((current) => ({ ...current, roleStatus: event.target.value as ProfessionalFinancialRoleStatus }))} className="w-full rounded-md border-gray-300 shadow-sm">
                <option value="active">Ativo</option>
                <option value="inactive">Inativo</option>
                <option value="expired">Expirado</option>
              </select>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div><label className="mb-1 block text-sm font-medium text-gray-700">Início da vigência</label><input type="date" value={form.effectiveFrom} onChange={(event) => setForm((current) => ({ ...current, effectiveFrom: event.target.value }))} className="w-full rounded-md border-gray-300 shadow-sm" /></div>
            <div><label className="mb-1 block text-sm font-medium text-gray-700">Fim da vigência</label><input type="date" value={form.effectiveUntil} onChange={(event) => setForm((current) => ({ ...current, effectiveUntil: event.target.value }))} className="w-full rounded-md border-gray-300 shadow-sm" /></div>
          </div>

          <div><label className="mb-1 block text-sm font-medium text-gray-700">Observações</label><textarea value={form.notes} onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))} className="w-full rounded-md border-gray-300 shadow-sm" rows={3} /></div>

          <div className="flex gap-3">
            <button type="submit" disabled={saving || !canEdit} className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50">{editingRoleId ? 'Atualizar' : 'Salvar'}</button>
            {editingRoleId && <button type="button" onClick={resetForm} className="rounded-md border px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancelar</button>}
          </div>
        </form>

        <div className="rounded-lg border bg-white p-6 shadow-sm">
          <h3 className="mb-4 text-lg font-semibold text-gray-800">Vínculos cadastrados</h3>
          {loading ? <p className="text-sm text-gray-500">Carregando...</p> : roles.length === 0 ? <p className="text-sm text-gray-500">Nenhum vínculo cadastrado.</p> : (
            <div className="space-y-3">
              {roles.map((role) => (
                <div key={role.financialRoleId} className="rounded-lg border p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="font-medium text-gray-900">{professionalNameById.get(role.professionalId) || role.professionalId}</p>
                      <p className="text-sm text-gray-600">{roleLabels[role.financialRoleType]} · {modelLabels[role.compensationModel]}</p>
                      <p className="text-sm text-gray-600">Vigência: {role.effectiveFrom}{role.effectiveUntil ? ` a ${role.effectiveUntil}` : ' em aberto'}</p>
                      {role.fixedAmount !== null && <p className="text-sm font-semibold text-primary-600">R$ {Number(role.fixedAmount).toFixed(2)}</p>}
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`rounded-full px-2 py-1 text-xs font-medium ${role.roleStatus === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>{role.roleStatus === 'active' ? 'Ativo' : role.roleStatus === 'expired' ? 'Expirado' : 'Inativo'}</span>
                      {canEdit && <><button type="button" onClick={() => editRole(role)} disabled={saving} className="rounded-md p-2 text-gray-600 hover:bg-gray-100 disabled:opacity-50" title="Editar" aria-label={`Editar vínculo de ${professionalNameById.get(role.professionalId) || 'profissional'}`}><Pencil className="h-4 w-4" /></button>
                      <button type="button" onClick={() => void toggleRole(role)} disabled={saving} className="rounded-md p-2 text-gray-600 hover:bg-gray-100 disabled:opacity-50" title="Alternar status">{role.roleStatus === 'active' ? <ToggleRight className="h-4 w-4 text-green-600" /> : <ToggleLeft className="h-4 w-4 text-gray-400" />}</button>
                      <button type="button" onClick={() => void removeRole(role)} disabled={saving} className="inline-flex items-center gap-1 rounded-md p-2 text-sm text-red-700 hover:bg-red-50 disabled:opacity-50" aria-label={`Excluir vínculo de ${professionalNameById.get(role.professionalId) || 'profissional'}`}><Trash2 className="h-4 w-4" />Excluir</button></>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProfessionalFinancialRolesPage;
